import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import logoIcon from '../../assets/logo-icon.png';
import {
  Shield,
  LayoutDashboard,
  Landmark,
  ArrowRightLeft,
  Users,
  Activity,
  Briefcase,
  Server,
  Receipt,
  BookOpen,
  HeartHandshake,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Bell,
  Search,
  ChevronDown,
  User,
  Wallet,
  Banknote,
  FileSearch,
  FileText,
} from 'lucide-react';

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  badge?: number;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

// Placeholder screens (Float, Settlements, Services, System Config, Audit,
// Reports) are intentionally omitted from this production build until the real
// screens ship. Their routes redirect to the dashboard (see RouteConfig).
const NAV_GROUPS: NavGroup[] = [
  {
    title: 'OVERVIEW',
    items: [{ label: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard }],
  },
  {
    title: 'OPERATIONS',
    items: [
      { label: 'Deposits', path: '/admin/unallocated-deposits', icon: Landmark },
      { label: 'Cash-in settlement', path: '/admin/cash-in-settlement', icon: Banknote },
      { label: 'Disbursements', path: '/admin/disbursements', icon: ArrowRightLeft },
      { label: 'Channel Partners', path: '/admin/channel-partners', icon: Users },
      { label: 'Partner settlement', path: '/admin/partner-settlement', icon: Wallet },
      { label: 'Clubs', path: '/admin/mmsp', icon: HeartHandshake },
      { label: 'Partner Onboarding', path: '/admin/partners', icon: Briefcase },
      { label: 'Treasury Debits', path: '/admin/treasury-outbound-debits', icon: Landmark },
    ],
  },
  {
    title: 'FINANCE',
    items: [
      { label: 'Audit evidence', path: '/admin/audit-evidence', icon: FileSearch },
      { label: 'Supplier statements', path: '/admin/supplier-statements', icon: FileText },
    ],
  },
  {
    title: 'MANAGEMENT',
    items: [
      { label: 'Users & KYC', path: '/admin/users', icon: Users },
      { label: 'Transactions', path: '/admin/transactions', icon: Activity },
      { label: 'FIC TFS', path: '/admin/fic-tfs', icon: Shield },
      { label: 'Catalog Governance', path: '/admin/catalog-governance', icon: Server },
      { label: 'Support KB', path: '/admin/support-kb', icon: BookOpen },
    ],
  },
  {
    title: 'GOVERNANCE & CONFIG',
    items: [
      { label: 'Fee Schedules', path: '/admin/fee-schedules', icon: Receipt },
      { label: 'Platform Health', path: '/admin/system', icon: Activity },
    ],
  },
];

const ENV_LABEL = import.meta.env.VITE_ENVIRONMENT || 'Development';
const IS_PRODUCTION_ENV = ENV_LABEL === 'Production';

/** Longer / more specific paths first */
const ROUTE_TITLE_RULES: { test: (pathname: string) => boolean; title: string }[] = [
  { test: (p) => p.startsWith('/admin/unallocated-deposits'), title: 'Unallocated Deposits' },
  { test: (p) => p.startsWith('/admin/cash-in-settlement'), title: 'Cash-in settlement' },
  { test: (p) => /^\/admin\/channel-partners\/\d+\/portal/.test(p), title: 'Partner Portal View' },
  { test: (p) => /^\/admin\/channel-partners\/\d+$/.test(p), title: 'Channel Partner' },
  { test: (p) => p.startsWith('/admin/mmsp'), title: 'Clubs' },
  { test: (p) => p.startsWith('/admin/channel-partners'), title: 'Channel Partners' },
  { test: (p) => p.startsWith('/admin/partner-settlement'), title: 'Partner settlement' },
  { test: (p) => p.startsWith('/admin/supplier-statements'), title: 'Supplier statements' },
  { test: (p) => p.startsWith('/admin/audit-evidence') || p.startsWith('/admin/finance'), title: 'Audit evidence' },
  { test: (p) => p.startsWith('/admin/disbursement-clients'), title: 'Channel Partners' },
  { test: (p) => p.startsWith('/admin/disbursements'), title: 'Disbursements' },
  { test: (p) => p.startsWith('/admin/treasury-outbound-debits'), title: 'Treasury Outbound Debits' },
  { test: (p) => p.startsWith('/admin/users'), title: 'Users & KYC' },
  { test: (p) => p.startsWith('/admin/transactions'), title: 'Transaction Monitoring' },
  { test: (p) => p.startsWith('/admin/fic-tfs'), title: 'FIC Targeted Financial Sanctions' },
  { test: (p) => p.startsWith('/admin/catalog-governance'), title: 'Catalog Governance' },
  { test: (p) => p.startsWith('/admin/support-kb'), title: 'Support KB' },
  { test: (p) => p.startsWith('/admin/partners'), title: 'Partner Onboarding' },
  { test: (p) => p.startsWith('/admin/fee-schedules'), title: 'Fee Schedules & Rates' },
  { test: (p) => p.startsWith('/admin/system/database'), title: 'Database health' },
  { test: (p) => p.startsWith('/admin/system/services'), title: 'Service health' },
  { test: (p) => p.startsWith('/admin/system'), title: 'Platform Health' },
  { test: (p) => p.startsWith('/admin/floats'), title: 'Supplier Float' },
  { test: (p) => p.startsWith('/admin/dashboard'), title: 'Dashboard' },
];

function getPageTitle(pathname: string): string {
  for (const rule of ROUTE_TITLE_RULES) {
    if (rule.test(pathname)) return rule.title;
  }
  return 'Admin Portal';
}

export const AppLayoutWrapper: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const pageTitle = useMemo(() => getPageTitle(location.pathname), [location.pathname]);

  const displayName = user?.entityName || user?.email || 'Admin';
  const displayRole = user?.role || 'admin';
  const initialLetter =
    (user?.entityName?.charAt(0) || user?.email?.charAt(0) || 'A').toUpperCase();

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
      setUserMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen, handleClickOutside]);

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--background)]">
      <aside
        className={`flex h-full flex-shrink-0 flex-col overflow-hidden border-r border-[var(--sidebar-border)] bg-[var(--sidebar)] transition-[width] duration-200 ease-in-out ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {/* Logo + environment */}
        <div
          className={`flex min-h-[44px] items-center gap-3 border-b border-[var(--sidebar-border)] py-4 ${
            collapsed ? 'justify-center px-2' : 'px-4'
          }`}
        >
          <img src={logoIcon} alt="MyMoolah" className="h-9 w-9 flex-shrink-0 rounded-lg" />
          <div
            className={`min-w-0 flex-1 overflow-hidden transition-opacity duration-200 ${
              collapsed ? 'pointer-events-none opacity-0' : 'opacity-100'
            }`}
          >
            <p className="truncate text-sm font-semibold text-white">MyMoolah</p>
            <span
              className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                IS_PRODUCTION_ENV
                  ? 'animate-pulse bg-red-500/20 text-red-300'
                  : 'bg-blue-500/20 text-blue-200'
              }`}
            >
              {ENV_LABEL}
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-5 overflow-y-auto px-2 py-4">
          {NAV_GROUPS.map((group) => (
            <div key={group.title}>
              <p
                className={`mb-2 px-2 text-[10px] font-medium uppercase tracking-widest text-[var(--muted-foreground)] transition-opacity duration-200 ${
                  collapsed ? 'pointer-events-none h-0 overflow-hidden opacity-0' : 'opacity-100'
                }`}
              >
                {group.title}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.path} className="relative">
                      <NavLink
                        to={item.path}
                        end={item.path === '/admin/dashboard'}
                        title={collapsed ? item.label : undefined}
                        aria-label={collapsed ? item.label : undefined}
                        className={({ isActive }) =>
                          [
                            'flex min-h-[44px] items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition-colors',
                            collapsed ? 'justify-center' : '',
                            isActive
                              ? 'bg-[var(--sidebar-accent)] font-medium text-white'
                              : 'text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)]',
                          ].join(' ')
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && (
                              <span
                                className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r bg-[var(--sidebar-primary)]"
                                aria-hidden
                              />
                            )}
                            <Icon className="h-[18px] w-[18px] flex-shrink-0" aria-hidden />
                            <span
                              className={`truncate transition-opacity duration-200 ${
                                collapsed ? 'pointer-events-none w-0 opacity-0' : 'opacity-100'
                              }`}
                            >
                              {item.label}
                            </span>
                            {!collapsed &&
                              item.badge !== undefined &&
                              item.badge > 0 && (
                                <span className="ml-auto inline-flex min-h-[20px] min-w-[20px] items-center justify-center rounded-full bg-amber-500/20 px-1.5 text-[10px] font-semibold text-amber-400">
                                  {item.badge}
                                </span>
                              )}
                          </>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Collapse toggle — between nav and user */}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="flex min-h-[44px] w-full items-center justify-center border-t border-[var(--sidebar-border)] text-[var(--sidebar-foreground)] transition-colors hover:bg-[var(--sidebar-accent)] hover:text-white"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4 flex-shrink-0" aria-hidden />
          ) : (
            <ChevronLeft className="h-4 w-4 flex-shrink-0" aria-hidden />
          )}
        </button>

        {/* User section */}
        <div className="border-t border-[var(--sidebar-border)] px-2 py-3">
          <div className={`flex min-h-[44px] items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
            <div
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[var(--sidebar-primary)] text-xs font-semibold text-[var(--sidebar-primary-foreground)]"
              aria-hidden
            >
              {initialLetter}
            </div>
            <div
              className={`min-w-0 flex-1 overflow-hidden transition-opacity duration-200 ${
                collapsed ? 'pointer-events-none w-0 opacity-0' : 'opacity-100'
              }`}
            >
              <p className="truncate text-sm text-white">{displayName}</p>
              <p className="truncate text-[11px] capitalize text-[var(--muted-foreground)]">
                {displayRole}
              </p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Sign out"
              title="Sign out"
              className="flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-lg text-[var(--sidebar-foreground)] transition-colors hover:bg-[var(--sidebar-accent)] hover:text-red-400"
            >
              <LogOut className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 flex-shrink-0 items-center justify-between border-b border-[var(--border)] bg-white px-4 sm:px-6">
          <h1 className="truncate text-base font-semibold text-[var(--foreground)]">{pageTitle}</h1>

          <div className="flex flex-shrink-0 items-center gap-2 sm:gap-3">
            <button
              type="button"
              aria-label="Search"
              className="flex h-11 w-11 items-center justify-center rounded-lg text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
            >
              <Search className="h-[18px] w-[18px]" aria-hidden />
            </button>
            <button
              type="button"
              aria-label="Notifications"
              className="flex h-11 w-11 items-center justify-center rounded-lg text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
            >
              <Bell className="h-[18px] w-[18px]" aria-hidden />
            </button>

            <div className="hidden h-6 w-px bg-[var(--border)] sm:block" aria-hidden />

            <div className="flex items-center gap-2 rounded-lg px-2 py-1.5">
              <span
                className="h-2 w-2 flex-shrink-0 rounded-full bg-[var(--success-color)]"
                aria-hidden
              />
              <span className="text-xs text-[var(--muted-foreground)]">System Online</span>
            </div>

            <div ref={userMenuRef} className="relative">
              <button
                type="button"
                onClick={() => setUserMenuOpen((o) => !o)}
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
                className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)] py-1 pl-1 pr-2 shadow-sm transition-colors hover:bg-[var(--muted)]"
              >
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[var(--sidebar-primary)] text-xs font-semibold text-[var(--sidebar-primary-foreground)]">
                  {initialLetter}
                </div>
                <div className="hidden min-w-0 max-w-[140px] text-left lg:block">
                  <p className="truncate text-xs font-medium text-[var(--foreground)]">{displayName}</p>
                  <p className="truncate text-[10px] capitalize text-[var(--muted-foreground)]">
                    {displayRole}
                  </p>
                </div>
                <ChevronDown
                  className={`h-4 w-4 flex-shrink-0 text-[var(--muted-foreground)] transition-transform duration-200 ${
                    userMenuOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden
                />
              </button>

              {userMenuOpen && (
                <div
                  className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] py-1 shadow-lg"
                  role="menu"
                >
                  <div className="border-b border-[var(--border)] px-4 py-3">
                    <p className="truncate text-sm font-medium text-[var(--foreground)]">{displayName}</p>
                    <p className="truncate text-xs text-[var(--muted-foreground)]">{user?.email}</p>
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); navigate('/admin/dashboard'); }}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-[var(--foreground)] transition-colors hover:bg-[var(--muted)]"
                  >
                    <User className="h-4 w-4 text-[var(--muted-foreground)]" aria-hidden />
                    My Profile
                  </button>
                  <div className="border-t border-[var(--border)]" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
                  >
                    <LogOut className="h-4 w-4" aria-hidden />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
